import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useApiClient } from "@/lib/api";
import { getUserErrorMessage } from "@/lib/error-messages";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Bot, Globe, Loader2, ShieldAlert, Wrench } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

const formSchema = z.object({
  title: z.string().min(1, "Job title is required"),
  company: z.string().min(1, "Company name is required"),
  description: z.string().optional(), // New optional description field
  raw_posting: z.string().min(50, "Full Listing Text must be at least 50 characters"), // Renamed from description
  location: z.string().optional(),
  remote: z.enum(["on-site", "hybrid", "remote"]),
  salary: z.string().optional(),
  listing_url: z.string().url().optional().or(z.literal('')),
});

type FormValues = z.infer<typeof formSchema>;
type PostingSource =
  | "manual"
  | "copy/pasted"
  | "extract_server"
  | "extracted_json"
  | "extracted_jina";
const isValidRemoteType = (
  value: string | undefined
): value is FormValues["remote"] =>
  value === "on-site" || value === "hybrid" || value === "remote";

const JobForm = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { api } = useApiClient();
  const isEditMode = !!jobId;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [inputMethod, setInputMethod] = useState<"manual" | "ai" | "url" | null>(null);
  const [jobListing, setJobListing] = useState("");
  const [jobListingUrl, setJobListingUrl] = useState("");
  const [isExtracting, setIsExtracting] = useState(false);
  const [urlExtractionHint, setUrlExtractionHint] = useState<string | null>(null);
  const [isBrowserFallbackRunning, setIsBrowserFallbackRunning] = useState(false);
  const [isJinaFallbackRunning, setIsJinaFallbackRunning] = useState(false);
  const [postingSource, setPostingSource] = useState<PostingSource>("manual");
  const [extractionMessages] = useState([
    "Reading through the job listing...",
    "Typing up the job spec...",
    "Analyzing key details...",
    "Almost done, polishing the results...",
    "Hmm... almost there...",
    "This job looks cool... let's see...",
    "Thanks - on it now...",
    "Working on it - one sec..."
  ]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  // For message rotation during extraction
  useEffect(() => {
    if (isExtracting) {
      const interval = setInterval(() => {
        const randomIndex = Math.floor(Math.random() * extractionMessages.length);
        setCurrentMessageIndex(randomIndex);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isExtracting, extractionMessages.length]);



  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      company: "",
      description: "", // New field
      raw_posting: "", // Renamed field
      location: "",
      remote: "on-site",
      salary: "",
      listing_url: "",
    },
  });

  // Load job data if in edit mode
  useEffect(() => {
    const fetchJob = async () => {
      if (!isEditMode) return;

      setIsLoading(true);
      try {
        const jobData = await api.getJobById(jobId!);
        form.reset({
          title: jobData.title,
          company: jobData.company,
          description: jobData.description || "", // Maps to new description field
          raw_posting: jobData.raw_posting || jobData.description, // Maps to new raw_posting field, fallback to old description if raw_posting not present
          location: jobData.location || "",
          remote: isValidRemoteType(jobData.remote)
            ? jobData.remote
            : "on-site",
          salary: jobData.salary || "",
          listing_url: jobData.listing_url || "",
        });
        setInputMethod("manual");
      } catch (error) {
        console.error("Error fetching job:", error);
        toast.error("Failed to load job details");
        navigate("/jobs");
      } finally {
        setIsLoading(false);
      }
    };

    fetchJob();
  }, [jobId, isEditMode]);

  // Handle AI extraction of job details
  const handleExtractJobDetails = async () => {
    if (!jobListing.trim()) {
      toast.error("Please paste a job listing first");
      return;
    }

    if (extractionMessages.length > 0) {
      const randomIndex = Math.floor(Math.random() * extractionMessages.length);
      setCurrentMessageIndex(randomIndex);
    }
    setIsExtracting(true);
    setUrlExtractionHint(null);
    try {
      const extractedJob = await api.extractJobFromListing(jobListing, "copy/pasted");

      form.reset({
        title: extractedJob.title || form.getValues("title"),
        company: extractedJob.company || form.getValues("company"), // Corrected: company_name -> company
        description: extractedJob.description || "", // AI's summarized description
        raw_posting: jobListing, // Full pasted listing for raw_posting
        location: extractedJob.location || form.getValues("location"),
        remote: extractedJob.remote?.toLowerCase() || form.getValues("remote"), // Corrected & normalized to lowercase
        salary: extractedJob.salary || form.getValues("salary"),
        listing_url: extractedJob.listing_url || form.getValues("listing_url"),
      });

      setInputMethod("manual"); // Switch to manual mode to show the form
      setPostingSource("copy/pasted");
      toast.success("Job details extracted successfully! Please review and make any necessary changes.");
    } catch (error: any) {
      console.error("Error extracting job details:", error);
      toast.error(
        getUserErrorMessage(
          error,
          "We couldn't extract job details right now. Please try again later or enter details manually."
        )
      );
    } finally {
      setIsExtracting(false);
    }
  };

  const handleExtractJobFromUrl = async () => {
    const trimmedUrl = jobListingUrl.trim();
    if (!trimmedUrl) {
      toast.error("Please enter a job URL first");
      return;
    }
    if (!/^https?:\/\//i.test(trimmedUrl)) {
      toast.error("Please enter a valid URL starting with http:// or https://");
      return;
    }

    if (extractionMessages.length > 0) {
      const randomIndex = Math.floor(Math.random() * extractionMessages.length);
      setCurrentMessageIndex(randomIndex);
    }
    setIsExtracting(true);
    setUrlExtractionHint(null);
    try {
      const extractedJob = await api.extractJobFromUrl(trimmedUrl);
      form.reset({
        title: extractedJob.title || form.getValues("title"),
        company: extractedJob.company || form.getValues("company"),
        description: extractedJob.description || "",
        raw_posting: extractedJob.raw_posting || form.getValues("raw_posting"),
        location: extractedJob.location || form.getValues("location"),
        remote: extractedJob.remote?.toLowerCase() || form.getValues("remote"),
        salary: extractedJob.salary || form.getValues("salary"),
        listing_url: extractedJob.listing_url || trimmedUrl,
      });
      setInputMethod("manual");
      setPostingSource("extract_server");
      toast.success("Job details extracted from URL. Please review before saving.");
    } catch (error: any) {
      const detail = error?.detail;
      const code = detail?.code as string | undefined;
      const message = getUserErrorMessage(
        detail?.message || error?.message,
        "We couldn't read this URL. Try again later or paste the listing text."
      );

      if (code?.startsWith("URL_FETCH_")) {
        setIsBrowserFallbackRunning(true);
        try {
          const fallbackJob = await extractJobViaBrowserFallback(trimmedUrl);
          form.reset({
            title: fallbackJob.title || form.getValues("title"),
            company: fallbackJob.company || form.getValues("company"),
            description: fallbackJob.description || "",
            raw_posting: fallbackJob.raw_posting || form.getValues("raw_posting"),
            location: fallbackJob.location || form.getValues("location"),
            remote: fallbackJob.remote?.toLowerCase() || form.getValues("remote"),
            salary: fallbackJob.salary || form.getValues("salary"),
            listing_url: trimmedUrl,
          });
          setInputMethod("manual");
          setPostingSource("extracted_json");
          toast.success("We pulled details from the page. Please review before saving.");
          return;
        } catch (fallbackError: any) {
          console.error("Browser fallback extraction failed:", fallbackError);
          setIsJinaFallbackRunning(true);
          try {
            const jinaFallbackJob = await api.extractJobFromUrlViaJina(trimmedUrl);
            form.reset({
              title: jinaFallbackJob.title || form.getValues("title"),
              company: jinaFallbackJob.company || form.getValues("company"),
              description: jinaFallbackJob.description || "",
              raw_posting: jinaFallbackJob.raw_posting || form.getValues("raw_posting"),
              location: jinaFallbackJob.location || form.getValues("location"),
              remote: jinaFallbackJob.remote?.toLowerCase() || form.getValues("remote"),
              salary: jinaFallbackJob.salary || form.getValues("salary"),
              listing_url: jinaFallbackJob.listing_url || trimmedUrl,
            });
            setInputMethod("manual");
            setPostingSource("extracted_jina");
            toast.success("We pulled details from the page via fallback extraction. Please review before saving.");
            return;
          } catch (jinaError: any) {
            console.error("Jina fallback extraction failed:", jinaError);
          } finally {
            setIsJinaFallbackRunning(false);
          }
        } finally {
          setIsBrowserFallbackRunning(false);
        }
      }

      if (code === "URL_FETCH_BLOCKED" || code === "JINA_FETCH_BLOCKED") {
        setUrlExtractionHint("We couldn’t read this page. Paste the listing text instead.");
      } else if (code === "NO_JOB_LISTING_FOUND") {
        setUrlExtractionHint("This page doesn’t look like a single job posting. Try another link or paste the listing text.");
      } else if (
        code === "URL_FETCH_UNSUPPORTED" ||
        code === "URL_FETCH_UNREADABLE" ||
        code === "JINA_FETCH_UNSUPPORTED" ||
        code === "JINA_FETCH_UNREADABLE"
      ) {
        setUrlExtractionHint("We couldn’t read enough from this page. Paste the full listing text instead.");
      } else if (code === "URL_FETCH_NOT_FOUND" || code === "JINA_FETCH_NOT_FOUND") {
        setUrlExtractionHint("This link looks unavailable right now. Check the URL or paste the listing text.");
      } else {
        setUrlExtractionHint("Try another source URL or paste the listing text directly.");
      }

      toast.error(message);
    } finally {
      setIsBrowserFallbackRunning(false);
      setIsJinaFallbackRunning(false);
      setIsExtracting(false);
    }
  };

  const extractTextFromHtml = (html: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    doc.querySelectorAll("script,style,noscript,svg,canvas").forEach((node) => node.remove());
    const text = doc.body?.innerText || doc.documentElement?.textContent || "";
    return text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .join("\n");
  };

  const extractJobViaBrowserFallback = async (url: string) => {
    const response = await fetch(url, {
      method: "GET",
      credentials: "omit",
      redirect: "follow",
      headers: {
        Accept: "text/html,application/xhtml+xml,text/plain;q=0.9,*/*;q=0.5",
      },
    });

    if (!response.ok) {
      throw new Error(`Browser fetch failed with status ${response.status}`);
    }

    const contentType = response.headers.get("content-type") || "";
    const bodyText = await response.text();
    const extractedText = contentType.includes("html")
      ? extractTextFromHtml(bodyText)
      : bodyText;

    if (extractedText.trim().length < 80) {
      throw new Error("Browser fallback could not read enough page content.");
    }

    return await api.extractJobFromListing(extractedText, "extracted_json");
  };

  const onSubmit = async (values: FormValues) => {
    console.log('Form submitted with values:', values);
    setIsSubmitting(true);
    try {
      const payload = {
        title: values.title,
        company: values.company,
        description: values.description || "", // Ensure it's at least an empty string if undefined
        raw_posting: values.raw_posting,
        location: values.location || "",
        remote: values.remote,
        salary: values.salary || "",
        listing_url: values.listing_url || "",
        posting_source: postingSource || "manual",
      };

      console.log('Submitting job data:', payload);
      if (isEditMode) {
        await api.updateJob(jobId!, payload);
        console.log('Job updated:', payload);
        toast.success("Job updated successfully");
        navigate("/jobs");
      } else {
        const createdJob = await api.createJob(payload);
        console.log('Job created:', createdJob);
        toast.success("Job created successfully");
        navigate(`/jobs/${createdJob.id}`);
      }
    } catch (error: any) {
      console.error("Error saving job:", error);
      toast.error(
        getUserErrorMessage(
          error,
          'Unable to save the job right now. Please try again later.'
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <Button variant="outline" size="sm" onClick={() => navigate("/jobs")} className="mb-6 self-start">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Jobs
        </Button>
        <div className="flex items-center justify-center flex-grow">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p>Loading job details...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // If not in edit mode and input method is not selected yet, show the selection screen
  if (!inputMethod && !isLoading && !isEditMode) {
    return (
      <DashboardLayout>
        <Button variant="outline" size="sm" onClick={() => navigate("/jobs")} className="mb-6 self-start">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Jobs
        </Button>
        <div className="flex flex-col items-center justify-start pt-2 p-4">
          <Card className="max-w-3xl w-full">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {/* AI Extraction Card (Left) */}
                <div
                  className="flex h-full cursor-pointer flex-col items-center rounded-lg p-6 text-center transition-colors hover:bg-slate-50 dark:hover:bg-slate-900"
                  onClick={() => setInputMethod("ai")}
                >
                  <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-full">
                    <Bot size={48} className="text-primary" />
                  </div>
                  <h3 className="mt-4 text-xl font-semibold">AI Extraction</h3>
                  <p className="mt-4 flex-1 text-muted-foreground">Paste a job listing and let our AI extract the details for you</p>
                  <Button onClick={() => setInputMethod("ai")} className="mt-4 w-full" variant="secondary">Use AI Assistant</Button>
                </div>
                <div
                  className="flex h-full cursor-pointer flex-col items-center rounded-lg border border-slate-100 p-6 text-center transition-colors hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-900"
                  onClick={() => setInputMethod("url")}
                >
                  <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-full">
                    <Globe size={48} className="text-primary" />
                  </div>
                  <h3 className="mt-4 text-xl font-semibold">From URL</h3>
                  <p className="mt-4 flex-1 text-muted-foreground">Paste a job link and we&apos;ll read and extract details with smart fallback</p>
                  <Button onClick={() => setInputMethod("url")} className="mt-4 w-full" variant="outline">Import From URL</Button>
                </div>
                {/* Manual Entry Card (Right) */}
                <div
                  className="flex h-full cursor-pointer flex-col items-center rounded-lg p-6 text-center transition-colors hover:bg-slate-50 dark:hover:bg-slate-900"
                  onClick={() => setInputMethod("manual")}
                >
                  <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-full">
                    <Wrench size={48} className="text-primary" />
                  </div>
                  <h3 className="mt-4 text-xl font-semibold">Manual Entry</h3>
                  <p className="mt-4 flex-1 text-muted-foreground">Fill in all the job details yourself using our form</p>
                  <Button onClick={() => setInputMethod("manual")} className="mt-4 w-full">Enter Manually</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // If AI extraction method is selected, show the paste interface
  if (inputMethod === "url" && !isEditMode) {
    return (
      <DashboardLayout>
        <Button variant="outline" size="sm" onClick={() => navigate("/jobs")} className="mb-6 self-start">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Jobs
        </Button>
        <div className="max-w-2xl mx-auto pb-8">
          <Card className="max-w-3xl w-full">
            <CardHeader>
              <CardTitle>Import Job From URL</CardTitle>
              <CardDescription>
                Paste a direct job listing URL. We&apos;ll check the page and extract details when possible.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Input
                  placeholder="https://company.com/careers/software-engineer"
                  value={jobListingUrl}
                  onChange={(e) => setJobListingUrl(e.target.value)}
                  disabled={isExtracting}
                  className="interview-input"
                />
                {(isBrowserFallbackRunning || isJinaFallbackRunning) && (
                  <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-blue-900 text-sm">
                    Reading page...
                  </div>
                )}
                {urlExtractionHint && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
                    <div className="flex items-start gap-2">
                      <ShieldAlert className="h-4 w-4 mt-0.5" />
                      <p className="text-sm">{urlExtractionHint}</p>
                    </div>
                    <div className="mt-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => setInputMethod("ai")}>
                        Paste Listing Text Instead
                      </Button>
                    </div>
                  </div>
                )}
                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setInputMethod(null)}
                    disabled={isExtracting}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleExtractJobFromUrl}
                    disabled={isExtracting || !jobListingUrl.trim()}
                  >
                    {isExtracting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {extractionMessages[currentMessageIndex]}
                      </>
                    ) : (
                      "Read URL & Extract"
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // If AI extraction method is selected, show the paste interface
  if (inputMethod === "ai" && !isEditMode) {
    return (
      <DashboardLayout>
        <Button variant="outline" size="sm" onClick={() => navigate("/jobs")} className="mb-6 self-start">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Jobs
        </Button>
        <div className="max-w-2xl mx-auto pb-8">
          <Card className="max-w-3xl w-full">
            <CardHeader>
              <CardTitle>AI Job Details Extraction</CardTitle>
              <CardDescription>
                Paste the job listing below and our AI will extract the key details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Textarea
                  placeholder="Paste the full job listing here..."
                  className="min-h-[300px]"
                  value={jobListing}
                  onChange={(e) => setJobListing(e.target.value)}
                  disabled={isExtracting}
                />
                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setInputMethod(null)}
                    disabled={isExtracting}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleExtractJobDetails}
                    disabled={isExtracting || !jobListing.trim()}
                  >
                    {isExtracting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {extractionMessages[currentMessageIndex]}
                      </>
                    ) : (
                      "Extract Job Details"
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Button variant="outline" size="sm" onClick={() => navigate("/jobs")} className="mb-6 self-start">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Jobs
      </Button>
      <div className="max-w-2xl mx-auto pb-8">
        <Card className="max-w-3xl w-full">
          <div className="absolute top-4 left-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => isEditMode ? navigate('/jobs') : setInputMethod(null)}
              className="h-8 w-8"
              title={isEditMode ? "Back to jobs" : "Back to options"}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
          <CardHeader className="pt-10">
            <CardTitle>
              {isEditMode ? "Update Job" : "Add Job"}
            </CardTitle>
            <CardDescription>
              {isEditMode
                ? "Update the job details to keep your listings current"
                : "Enter the job details to generate tailored interview questions"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form id="job-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Job Title*</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. Software Engineer"
                            className="interview-input"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company*</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. Acme Corp"
                            className="interview-input"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Location</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. New York, NY"
                            className="interview-input"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="salary"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Salary Range</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. $80,000 - $100,000"
                            className="interview-input"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="remote"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel>Work Type</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          value={field.value}
                          className="flex flex-row space-x-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="on-site" id="on-site" />
                            <Label htmlFor="on-site">On-site</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="hybrid" id="hybrid" />
                            <Label htmlFor="hybrid">Hybrid</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="remote" id="remote" />
                            <Label htmlFor="remote">Remote</Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="listing_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Listing URL</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. https://company.com/job-listing"
                          className="interview-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Job Description (Optional)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="A brief summary or key highlights of the job (e.g., from the top of the job post)."
                          className="interview-input min-h-[100px]"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        This can be a short summary. The full, raw listing text goes in the field below.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="raw_posting"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Listing Text*</FormLabel>
                      <FormDescription>
                        Paste the entire, raw job listing text here. This is important for generating accurate interview questions.
                      </FormDescription>
                      <FormControl>
                        <Textarea
                          placeholder="Paste the full job listing text here..."
                          className="interview-input min-h-[200px]"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Ensure this includes all details from the original job posting.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => isEditMode ? navigate("/jobs") : setInputMethod(null)}
            >
              Back
            </Button>
            <Button type="submit" form="job-form" disabled={isSubmitting}>
              {isSubmitting
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                  ? "Update Job"
                  : "Create Job"}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default JobForm;
